// ScenarioIQ API Service
import { msalInstance, apiRequest, loginRequest } from '../authConfig';
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { setForbidden } from './permissionState';
import {
  ScenarioResultsResponse,
  UserProductFamiliesResponse,
  ScenarioResult,
  ApiResponse,
  PayerGroupingsResponse,
  ScenarioRequestWithMinsAndMaxAndPvt,
  PayerMixResponse,
  SaveFavoriteScenarioRequest,
  UpdateFavoriteScenarioRequest,
  BaseResponse,
  FavoriteScenariosResponse,
  DrugPayerMatrixResponse,
  PayerMixRequest,
  ExcludedOpportunity,
  UserPracticesResponse,
  PayerMixCategoryRequest,
  PayerMixByCategoryResponse,
  FeeSchedulesResponse,
  FeeScheduleRelevantJCodesResponse,
  FeeScheduleStageRequest,
  FeeScheduleStageResponse,
  FeeScheduleBulkRequest,
  FeeScheduleCoverageResponse,
  PayerGroupRateRulesResponse,
  PayerGroupingReconciliation,
  RebateDetail,
  ProductComparisonResponse,
} from '../types';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Most calls are quick; a stuck one should fail within a minute, not leave a spinner running.
// Generation and exports override this per call.
const DEFAULT_TIMEOUT_MS = 60_000;
const GENERATION_TIMEOUT_MS = 20 * 60_000;
// Calls that can legitimately take minutes: exports, bulk fee-schedule writes, saves that fan
// out across areas or rows, and the fee-schedule read that refreshes rates at a quarter turn.
const SLOW_CALL_TIMEOUT_MS = 3 * 60_000;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Rejection used while the app is leaving for the sign-in page. */
class ReauthenticatingError extends Error {
  constructor() {
    super('Your session has expired. Redirecting to sign in…');
    this.name = 'ReauthenticatingError';
  }
}

// One sign-in redirect at a time: several requests can need it at once, and a second
// loginRedirect/acquireTokenRedirect while one is in progress throws interaction_in_progress.
let redirectInFlight = false;
const redirectToSignIn = (account: ReturnType<typeof msalInstance.getActiveAccount>): Promise<never> => {
  if (!redirectInFlight) {
    redirectInFlight = true;
    const start = account
      ? msalInstance.acquireTokenRedirect({ ...apiRequest, account })
      : msalInstance.loginRedirect(loginRequest);
    start.catch((err) => {
      redirectInFlight = false;
      // Another part of the app already started the interaction; it will navigate away.
      if ((err as { errorCode?: string })?.errorCode !== 'interaction_in_progress') {
        console.error('Sign-in redirect failed:', (err as Error)?.message ?? err);
      }
    });
  }
  return Promise.reject(new ReauthenticatingError());
};

/** The signed-in account. MSAL can hold accounts without an "active" one (e.g. a new tab). */
const currentAccount = () => {
  const active = msalInstance.getActiveAccount();
  if (active) return active;
  const [first] = msalInstance.getAllAccounts();
  if (first) msalInstance.setActiveAccount(first);
  return first ?? null;
};

const attachToken = async (config: InternalAxiosRequestConfig, forceRefresh = false) => {
  const account = currentAccount();
  if (!account) return redirectToSignIn(null);
  try {
    const response = await msalInstance.acquireTokenSilent({ ...apiRequest, account, forceRefresh });
    if (response.accessToken) {
      config.headers.Authorization = `Bearer ${response.accessToken}`;
    }
    return config;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) return redirectToSignIn(account);
    throw error;
  }
};

apiClient.interceptors.request.use((config) => attachToken(config));

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401: the token was rejected (expired or revoked). Retry once with a freshly issued token;
    // if that is rejected too, the session is over, so sign in again.
    const config = error?.config as (InternalAxiosRequestConfig & { retriedAfter401?: boolean }) | undefined;
    if (error?.response?.status === 401 && config) {
      if (!config.retriedAfter401) {
        config.retriedAfter401 = true;
        try {
          await attachToken(config, true);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
        return apiClient(config);
      }
      return redirectToSignIn(currentAccount());
    }
    // Only a missing role (ROLE_REQUIRED) or an inactive account (ACCOUNT_INACTIVE) swaps the
    // app for the no-permission page. Other 403s (an account the user isn't assigned to, a WAF
    // block) stay per-request errors: treating them as "no role" locked users out until logout.
    // The message match covers a backend deployed before these codes existed.
    if (error?.response?.status === 403) {
      const data = error.response.data ?? {};
      const locksApp = data.code === 'ROLE_REQUIRED' || data.code === 'ACCOUNT_INACTIVE'
        || (data.code === undefined && data.detail === 'You do not have permission to access this resource.');
      if (locksApp) setForbidden(true);
    }
    return Promise.reject(error);
  }
);

/**
 * Compact result rows into a columnar payload: `{ columns, rows }`, where each
 * key name is transmitted once (in `columns`) instead of repeated on every row.
 *
 * The export body is otherwise dominated by ~20 long repeated JSON keys per row
 * (e.g. `estimated_medicare_reimbursement_per_eu`) across ~190 rows, which pushes
 * scaled-up exports over the Azure Front Door / WAF 128 KB request-body limit and
 * gets them blocked in production. Columnar form roughly halves the body with no
 * change to the exported data — the backend reconstructs the same DataFrame.
 *
 * Column order is the first-seen union of keys across all rows (matching how
 * pandas builds a DataFrame from a list of dicts); missing values become null.
 */
function toColumnar<T>(items: T[]): { columns: string[]; rows: unknown[][] } {
  const columns: string[] = [];
  const seen = new Set<string>();
  for (const row of items) {
    for (const key of Object.keys(row as unknown as Record<string, unknown>)) {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    }
  }
  const rows = items.map((row) => {
    const r = row as unknown as Record<string, unknown>;
    return columns.map((c) => (r[c] === undefined ? null : r[c]));
  });
  return { columns, rows };
}

function toColumnarExportPayload(
  results: ScenarioResult[]
): { columns: string[]; rows: unknown[][] } {
  return toColumnar(results);
}

/**
 * Rebate rows for the export's extra sheet. Far more numerous than the result rows, so they go
 * columnar for the same body-size reason. Rows the backend would discard are dropped here.
 */
function toRebateDetailPayload(
  rebateDetails: RebateDetail[] | undefined | null
): { columns: string[]; rows: unknown[][] } | undefined {
  const usable = (rebateDetails ?? []).filter(
    (d) => d.has_contract || d.rebate_source === 'Excluded'
  );
  return usable.length > 0 ? toColumnar(usable) : undefined;
}

// API Service Class
export class ApiService {
  static async generateAndCalculateScenariosWithMinsAndMaxAndPvt(request: ScenarioRequestWithMinsAndMaxAndPvt): Promise<ScenarioResultsResponse> {
    const response: AxiosResponse<ScenarioResultsResponse> = await apiClient.post(
      '/scenarios/generate-and-calculate-with-mins-and-max-and-pvt',
      request,
      { timeout: GENERATION_TIMEOUT_MS },
    );
    return response.data;
  }

  static async generateAndCalculateScenariosWithMinsAndMaxAndPvtGreedy(request: ScenarioRequestWithMinsAndMaxAndPvt): Promise<ScenarioResultsResponse> {
    const response: AxiosResponse<ScenarioResultsResponse> = await apiClient.post(
      '/scenarios/generate-greedy-by-payer-and-calculate-with-pvt',
      request,
      { timeout: GENERATION_TIMEOUT_MS },
    );
    return response.data;
  }

// DEVELOPMENTAL ENDPOINT, STILL TO BE RESEARCHED AND TESTED
  // static async generateAndCalculateScenariosBandits(request: ScenarioRequestWithMinsAndMax): Promise<ScenarioResultsResponse> {
  //   const response: AxiosResponse<ScenarioResultsResponse> = await apiClient.post(
  //     '/scenarios/payer-bandit-search',
  //     request
  //   );
  //   return response.data;
  // }

  static async getScenarioExpertSummary(message: string): Promise<{ response: string }> {
    const response: AxiosResponse<{ response: string }> = await apiClient.post(
      '/scenarios/chat',
      { message }
    );
    return response.data;
  }

  static async getPayerMix(request: PayerMixRequest): Promise<PayerMixResponse> {
    const response: AxiosResponse<PayerMixResponse> = await apiClient.get(
      '/scenarios/get-payer-mix',
      { params: request }
    );
    return response.data;
  }

  static async getPayerMixByCategory(request: PayerMixCategoryRequest): Promise<PayerMixByCategoryResponse> {
    const { logged_in_user, category } = request;
    const response: AxiosResponse<PayerMixResponse> = await apiClient.get(
      `/users/category-payer-mix/${encodeURIComponent(logged_in_user)}`,
      { params: { category } }
    );
    return response.data;
  }

  // Last quarter's actual total category units for a category (the "Current" total).
  // Lets the generator show the modeled unit count before any scenarios are generated.
  static async getBaselineCategoryUnits(
    loggedInUser: string,
    category: string
  ): Promise<number> {
    const response: AxiosResponse<{
      logged_in_user: string;
      category: string;
      category_units: number;
    }> = await apiClient.get('/scenarios/baseline-category-units', {
      params: { logged_in_user: loggedInUser, category },
    });
    return Number(response.data?.category_units ?? 0);
  }

  // Drug ↔ Payer matrix per category
  static async getDrugPayerMatrix(
    userId: string,
    category: string
  ): Promise<DrugPayerMatrixResponse> {
    // Mirror payer mix pattern: send logged_in_user & category as query params to a fixed endpoint
    // Backend route expected: @router.get("/payer-preferences") mounted under /scenarios
    const response: AxiosResponse<any> = await apiClient.get(
      '/scenarios/payer-preferences',
      { params: { logged_in_user: userId, category } }
    );
    const raw = response.data;
    // Normalize backend 'preferences' to frontend 'drugPayerMatrix'
    const prefs = raw.preferences || raw.drugPayerMatrix || {};
    return {
      category: raw.category || category,
      drugPayerMatrix: prefs,
      preferences: prefs,
    } as DrugPayerMatrixResponse;
  }

  static async saveDrugPayerMatrix(
    userId: string,
    drugPayerMatrix: { [drugId: string]: { [payer: string]: boolean } }
  ): Promise<ApiResponse<{ saved: boolean }>> {
    // Backend expects 'preferences'; include both for backward compatibility
    const response: AxiosResponse<ApiResponse<{ saved: boolean }>> = await apiClient.post(
      '/scenarios/payer-preferences',
      { logged_in_user: userId, preferences: drugPayerMatrix, drugPayerMatrix }
    );
    return response.data;
  }

  static async exportScenarioResults(
    results: ScenarioResult[],
    format: 'csv' | 'excel' = 'csv',
    rebateDetails?: RebateDetail[] | null,
    isOral = false
  ): Promise<Blob> {
    // Send a columnar payload (keys once, not per row) so the request body stays
    // well under the Azure Front Door / WAF 128 KB limit even for scaled-up runs.
    const rebate = toRebateDetailPayload(rebateDetails);
    const response = await apiClient.post(
      `/scenarios/export?format=${format}`,
      {
        ...toColumnarExportPayload(results),
        ...(rebate ? { rebate_details: rebate } : {}),
        ...(isOral ? { is_oral: true } : {}),
      },
      {
        responseType: 'blob',
        timeout: SLOW_CALL_TIMEOUT_MS,
      }
    );
    return response.data;
  }

  static async exportScenarioResultsWithPayerAssignments(
    results: ScenarioResult[],
    payerAssignments: Array<{ scenario_id: number; payer: string; product_family_name: string; payer_percent?: number }>,
    format: 'csv' | 'excel' = 'csv',
    rebateDetails?: RebateDetail[] | null,
    isOral = false
  ): Promise<Blob> {
    const rebate = toRebateDetailPayload(rebateDetails);
    const response = await apiClient.post(
      `/scenarios/export?format=${format}`,
      {
        ...toColumnarExportPayload(results),
        payer_assignments: payerAssignments,
        ...(rebate ? { rebate_details: rebate } : {}),
        ...(isOral ? { is_oral: true } : {}),
      },
      {
        responseType: 'blob',
        timeout: SLOW_CALL_TIMEOUT_MS,
      }
    );
    return response.data;
  }

  static async saveFavoriteScenario(data: SaveFavoriteScenarioRequest): Promise<BaseResponse> {
    try {
      const response = await apiClient.post<BaseResponse>('/scenarios/save-favorite-scenario', data, { timeout: SLOW_CALL_TIMEOUT_MS });
      return response.data;
    } catch (error) {
      console.error('Failed to save favorite scenario:', error);
      throw error;
    }
  }

  static async updateFavoriteScenario(data: UpdateFavoriteScenarioRequest): Promise<BaseResponse> {
    if (!data.user_id) {
      console.error('Saved scenario is missing user_id');
    }
    try {
      const response = await apiClient.put<BaseResponse>('/scenarios/update-favorite-scenario', data);
      return response.data;
    } catch (error) {
      console.error('Failed to update favorite scenario:', error);
      throw error;
    }
  }

  static async getFavoriteScenarios(userId: string): Promise<FavoriteScenariosResponse> {
    try {
      const response = await apiClient.get<FavoriteScenariosResponse>(`/scenarios/get-favorite-scenarios/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch favorite scenarios:', error);
      throw error;
    }
  }

  static async getCategoryProductFamilies(
    userId: string,
    category: string
  ): Promise<UserProductFamiliesResponse> {
    const response: AxiosResponse<UserProductFamiliesResponse> = await apiClient.get(
      `/users/${userId}/product-families-all`,
      { params: { category } } // query param, automatically encoded
    );
    return response.data;
  }

  static async getCategoryProductFamiliesNames(
    userId: string,
    category: string
  ): Promise<UserProductFamiliesResponse> {
    const response: AxiosResponse<UserProductFamiliesResponse> = await apiClient.get(
      `/users/${userId}/product-families-names-all`,
      { params: { category } } // query param, automatically encoded
    );
    return response.data;
  }

  static async getUserCategories(userId: string): Promise<ApiResponse<{ categories: string[] }>> {
    const response: AxiosResponse<ApiResponse<{ categories: string[] }>> = await apiClient.get(
      `/users/${userId}/categories`
    );
    return response.data;
  }

  static async getUserName(userId: string): Promise<ApiResponse<{ user_name: string }>> {
  const response: AxiosResponse<ApiResponse<{ user_name: string }>> = await apiClient.get(
    `/users/${userId}/name`
  );
  return response.data;
  }

  static async getPayerGroupings(logged_in_user: string): Promise<PayerGroupingsResponse> {
    const response: AxiosResponse<PayerGroupingsResponse> = await apiClient.get(
      `/users/payer-groupings/${encodeURIComponent(logged_in_user)}`
    );
    return response.data;
  }

  static async getPayerGroupingsByCategory(logged_in_user: string, category: string): Promise<PayerGroupingsResponse> {
    const response: AxiosResponse<PayerGroupingsResponse> = await apiClient.get(
      `/users/payer-groupings/${encodeURIComponent(logged_in_user)}/by-category`,
      { params: { category } }
    );
    return response.data;
  }

  static async savePayerGroupings(
    logged_in_user: string,
    payer_groupings: Record<string, string[]>,
    reconciliation?: PayerGroupingReconciliation
  ): Promise<ApiResponse<null>> {
    const body: {
      payer_groupings: Record<string, string[]>;
      reconciliation?: PayerGroupingReconciliation;
    } = { payer_groupings };
    if (reconciliation) body.reconciliation = reconciliation;
    const response: AxiosResponse<ApiResponse<null>> = await apiClient.post(
      `/users/payer-groupings/${encodeURIComponent(logged_in_user)}`,
      body,
      { timeout: SLOW_CALL_TIMEOUT_MS },
    );
    return response.data;
  }

  static async getUserId(userName: string): Promise<ApiResponse<{ user_id: string }>> {
  const response: AxiosResponse<ApiResponse<{ user_id: string }>> = await apiClient.get(
    `/users/name/${encodeURIComponent(userName)}/id`
  );
  return response.data;
  }

  static async getUserPractices(): Promise<UserPracticesResponse> {
    const response: AxiosResponse<UserPracticesResponse> = await apiClient.get(
      '/practices',
      // Practices is the application gate. Do not leave the whole UI spinning
      // behind the generation endpoint's intentionally long timeout.
      { timeout: 30000 }
    );
    return response.data;
  }

  static async getExcludedOpportunities(userId: string): Promise<ApiResponse<ExcludedOpportunity[]>> {
    const response: AxiosResponse<ApiResponse<ExcludedOpportunity[]>> = await apiClient.get(
      `/users/excluded-opportunities/${userId}`
    );
    return response.data;
  }

  /**
   * Current-mix economics for Product Comparison. This is deliberately a
   * dedicated calculation rather than a client-side reconstruction so price,
   * rebate and equivalent-unit logic stay aligned with ScenarioIQ.
   */
  static async getProductComparison(
    loggedInUser: string,
    category: string,
    signal?: AbortSignal
  ): Promise<ProductComparisonResponse> {
    const response: AxiosResponse<ProductComparisonResponse> = await apiClient.get(
      '/product-groups/comparison',
      {
        params: { logged_in_user: loggedInUser, category },
        signal,
        // Product economics can be expensive, but leaving this screen in a
        // spinner for the global 20-minute API timeout is not a usable state.
        // The Product Comparison page shows a retryable error instead.
        timeout: 90000,
      }
    );
    return response.data;
  }

  static async checkApplicationHealth(): Promise<ApiResponse<never>> {
    const response: AxiosResponse<ApiResponse<never>> = await apiClient.get('/health');
    return response.data;
  }

  // Fee Schedules
  static async getFeeSchedules(loggedInUser: string): Promise<FeeSchedulesResponse> {
    const response: AxiosResponse<FeeSchedulesResponse> = await apiClient.get(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}`,
      { timeout: SLOW_CALL_TIMEOUT_MS },
    );
    return response.data;
  }

  // DISABLED (2026-09-28): the POST /fee-schedules/{user}/synthetic-asp route is
  // commented out on the backend and this had no remaining callers, so leaving it
  // callable would only hand someone a 404.
  // static async createSyntheticAspFeeSchedule(
  //   loggedInUser: string,
  //   payerName: string,
  //   aspMarkupPercent: number,
  //   category?: string,
  // ): Promise<ApiResponse<never>> {
  //   const response: AxiosResponse<ApiResponse<never>> = await apiClient.post(
  //     `/fee-schedules/${encodeURIComponent(loggedInUser)}/synthetic-asp`,
  //     { payer_name: payerName, asp_markup_percent: aspMarkupPercent, category },
  //   );
  //   return response.data;
  // }

  static async applyPublishedLimitMultiplierBulk(
    loggedInUser: string,
    multipliers: Record<string, number>,
  ): Promise<ApiResponse<never>> {
    const response: AxiosResponse<ApiResponse<never>> = await apiClient.post(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/published-limit-multiplier/bulk`,
      { multipliers },
      { timeout: SLOW_CALL_TIMEOUT_MS },
    );
    return response.data;
  }

  static async getPublishedLimitMultipliers(
    loggedInUser: string,
  ): Promise<PayerGroupRateRulesResponse> {
    const response: AxiosResponse<PayerGroupRateRulesResponse> = await apiClient.get(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/published-limit-multiplier`,
    );
    return response.data;
  }

  static async getRelevantJCodes(
    loggedInUser: string
  ): Promise<FeeScheduleRelevantJCodesResponse> {
    const response: AxiosResponse<FeeScheduleRelevantJCodesResponse> = await apiClient.get(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/relevant-jcodes`
    );
    return response.data;
  }

  static async getFeeScheduleCoverage(
    loggedInUser: string
  ): Promise<FeeScheduleCoverageResponse> {
    const response: AxiosResponse<FeeScheduleCoverageResponse> = await apiClient.get(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/fee-schedule-coverage`
    );
    return response.data;
  }

  static async updateFeeSchedule(
    id: number,
    reimbursements: Record<string, number>
  ): Promise<BaseResponse> {
    const response: AxiosResponse<BaseResponse> = await apiClient.put(
      `/fee-schedules/${id}`,
      { reimbursements }
    );
    return response.data;
  }

  static async deleteFeeSchedule(id: number): Promise<BaseResponse> {
    const response: AxiosResponse<BaseResponse> = await apiClient.delete(
      `/fee-schedules/${id}`
    );
    return response.data;
  }

  static async stageFeeScheduleUpload(
    loggedInUser: string,
    payload: FeeScheduleStageRequest
  ): Promise<FeeScheduleStageResponse> {
    const response: AxiosResponse<FeeScheduleStageResponse> = await apiClient.post(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/stage`,
      payload,
      { timeout: SLOW_CALL_TIMEOUT_MS },
    );
    return response.data;
  }

  static async bulkCommitFeeSchedules(
    loggedInUser: string,
    payload: FeeScheduleBulkRequest
  ): Promise<BaseResponse> {
    const response: AxiosResponse<BaseResponse> = await apiClient.post(
      `/fee-schedules/${encodeURIComponent(loggedInUser)}/bulk`,
      payload,
      { timeout: SLOW_CALL_TIMEOUT_MS },
    );
    return response.data;
  }
}

// Utility functions for error handling
export const handleApiError = (error: any): string => {
  if (error instanceof ReauthenticatingError) return error.message;
  if (error?.code === 'ECONNABORTED') return 'The server took too long to respond. Please try again.';
  if (error?.response?.status === 413) return 'This request is too large to send. Try fewer scenarios at a time.';
  if (!error?.response && error?.message === 'Network Error') {
    return "Couldn't reach the server. Check your connection and try again.";
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * handleApiError for requests made with responseType 'blob' (exports): the server's JSON error
 * body arrives as a Blob, so read it first or every failure looks the same.
 */
export const handleApiErrorAsync = async (error: any): Promise<string> => {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      error.response.data = JSON.parse(await data.text());
    } catch {
      // Not a JSON body; fall back to the status-based messages.
    }
  }
  return handleApiError(error);
};

export const isApiError = (error: any): boolean => {
  return error.response && error.response.status >= 400;
};

// Export default instance
export default ApiService;
